import { MarkdownProse } from "@/components/ui/markdown-prose";
import { Link2, Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SeverityDot, severityTone } from "@/components/ui/severity-dot";
import type { Severity } from "@/components/finding-badges";
import { TargetCropThumbnail } from "@/components/walkthrough/zen-mode/target-crop-thumbnail";
import type { ComparisonFinding } from "./comparison-types";

export type ComparisonFindingVariant =
  | "new_problem"
  | "new_strength"
  | "resolved"
  | "lost_strength"
  | "shifted"
  | "persisting";

export interface ComparisonFindingRowProps {
  finding: ComparisonFinding;
  variant: ComparisonFindingVariant;
  onClick: () => void;
  previousSeverity?: string | null;
  newSeverity?: string | null;
  canEdit?: boolean;
  isTrackerConfigured?: boolean;
  onCreateIssue?: (canonicalId: string | null) => void;
  onLinkIssue?: (canonicalId: string) => void;
}

export function ComparisonFindingRow({
  finding,
  variant,
  onClick,
  previousSeverity,
  newSeverity,
  canEdit,
  isTrackerConfigured,
  onCreateIssue,
  onLinkIssue,
}: ComparisonFindingRowProps) {
  const showTrackerActions =
    canEdit &&
    isTrackerConfigured &&
    (variant === "new_problem" || variant === "persisting");

  return (
    <div
      onClick={onClick}
      className="p-3.5 hover:bg-muted/20 cursor-pointer transition-colors space-y-2 group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          {/* Variant Delta Badge */}
          {variant === "new_problem" && (
            <span className="inline-flex items-center gap-1 rounded-sm bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
              + New
            </span>
          )}
          {variant === "new_strength" && (
            <span className="inline-flex items-center gap-1 rounded-sm bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
              + Strength
            </span>
          )}
          {variant === "resolved" && (
            <span className="inline-flex items-center gap-1 rounded-sm bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
              <Check className="size-3" />
              <span>Resolved</span>
            </span>
          )}
          {variant === "lost_strength" && (
            <span className="inline-flex items-center gap-1 rounded-sm bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
              <span>Strength lost</span>
            </span>
          )}
          {variant === "shifted" && (
            <span className="inline-flex items-center gap-1 rounded-sm bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
              ~ Shifted
            </span>
          )}

          {/* Severity Dot */}
          {finding.severity && (
            <SeverityDot tone={severityTone(finding.severity as Severity)} />
          )}

          {/* Title */}
          <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
            {finding.title || finding.description.slice(0, 70)}
          </span>

          {/* Previous Severity Note */}
          {variant === "resolved" && previousSeverity && (
            <span className="text-xs text-muted-foreground font-mono">
              (was {previousSeverity})
            </span>
          )}

          {/* Shift Details */}
          {variant === "shifted" && previousSeverity && newSeverity && (
            <span className="text-xs font-mono text-muted-foreground ml-auto pr-2">
              <span className="capitalize">{previousSeverity}</span> →{" "}
              <span className="capitalize font-semibold text-foreground">
                {newSeverity}
              </span>
            </span>
          )}

          {finding.stepNumber && (
            <span className="text-xs text-muted-foreground font-mono">
              Step {finding.stepNumber}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Spatial target thumbnail */}
          {finding.walkthroughId && typeof finding.stepNumber === "number" && (
            <div
              className="hidden sm:block h-9 w-14 shrink-0 overflow-hidden rounded border border-border/60 hover:border-border hover:shadow-xs transition-all"
              title={`Step ${finding.stepNumber} target area`}
            >
              <TargetCropThumbnail
                walkthroughId={finding.walkthroughId}
                stepNumber={finding.stepNumber}
                targetBox={finding.targetBox}
                targetTone={finding.severity || finding.type}
                alt={`Step ${finding.stepNumber} target preview`}
                className="h-full w-full"
              />
            </div>
          )}

          {/* Issue Tracker Buttons */}
          {showTrackerActions && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1 shrink-0"
            >
              {onCreateIssue && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() =>
                    onCreateIssue(finding.canonicalFindingId ?? null)
                  }
                >
                  Create issue…
                </Button>
              )}
              {onLinkIssue && finding.canonicalFindingId && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onLinkIssue(finding.canonicalFindingId!)}
                >
                  <Link2 className="mr-1 size-3" />
                  Link…
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Observation Facts */}
      <div className="text-xs space-y-1 text-muted-foreground pl-0.5">
        {finding.uiFact && (
          <p>
            <strong className="text-foreground font-medium">UI:</strong>{" "}
            {finding.uiFact}
          </p>
        )}
        {finding.consequence && (
          <p>
            <strong className="text-foreground font-medium">Impact:</strong>{" "}
            {finding.consequence}
          </p>
        )}
        {!finding.uiFact && !finding.consequence && (
          <p className="line-clamp-2">{finding.description}</p>
        )}
      </div>

      {/* Suggestion Callout */}
      {finding.recommendation && (
        <div className="flex items-start gap-1.5 text-xs text-foreground bg-surface-tonal rounded-sm px-2.5 py-1.5">
          <Sparkles className="size-3.5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="min-w-0">
            <strong className="font-semibold">Suggestion:</strong>
            <MarkdownProse
              content={finding.recommendation}
              lineBreaks
              className="text-xs prose-p:my-1"
            />
          </div>
        </div>
      )}
    </div>
  );
}
